import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";

//PACKAGES
import { useNavigate } from "react-router-dom";
import moment from "moment";

//CONSTANTS
import { STRING, STRINGS } from "../constant";

//COMPONENTS
import { Header, SpeedoMeter, Chart } from "../components";
import { AuthContext } from "../context/AuthProvider";
import { ThemeContext } from "../context/ThemeProvider";
import { MixpanelContext } from "../context/MixpanelProvider";
import { getThemeTextColor } from "../utils/themeUtils";
import useFeatureList from "../hooks/useFeatureList";
import { createLogger } from "../utils/logger";

// Card animations
const cardVariants = {
  hover: { 
    scale: 1.02,
    boxShadow: "0px 8px 15px rgba(0,0,0,0.1)",
    transition: { type: "spring", stiffness: 400, damping: 10 }
  }
};

function Dashboard() {
  const { profile, loggingEnabled } = useContext(AuthContext);
  const navigation = useNavigate();
  const { trackDashboard } = useContext(MixpanelContext);
  const { theme, currentTheme } = useContext(ThemeContext);
  const [score, setScore] = useState(null);
  const [scoreProgress, setScoreProgress] = useState({});
  const featureList = useFeatureList();
  
  const logger = createLogger(loggingEnabled);

  useEffect(() => {
    const loadData = async () => {
      trackDashboard();
      const profileData = await profile;
      logger.log("[Dashboard] Profile Data:", profileData);
      logger.log("[Dashboard] Readiness Scores:", profileData?.readiness_scores);
      logger.log("[Dashboard] Score Progress:", profileData?.score_progress);
      setScore(profileData?.readiness_scores);
      setScoreProgress(profileData?.score_progress ?? {});
    };
    loadData();
  }, [profile]);

  let keys = Object.keys(scoreProgress);

  const getProgressBarColor = (readinessScore) => {
    if (readinessScore < 25) return "bg-red-500";
    if (readinessScore >= 25 && readinessScore <= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  // Helper function to get card background
  const getCardBackground = () => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark;
    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
    };
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  function getChartData() {
    keys.sort(
      (a, b) =>
        moment(a, "YYYY-MM-DD").toDate().getTime() -
        moment(b, "YYYY-MM-DD").toDate().getTime()
    );

    if (keys.length > 6) {
      keys = keys.slice(keys.length - 6, keys.length);
    }

    let labels = keys.length < 6 ? [""] : [];
    let data = keys.length < 6 ? [0] : [];
    for (var index in keys) {
      if (index <= 5) {
        const key = keys[index];
        labels.push(moment(key, "YYYY-MM-DD").format("DD MMM"));

        const score = scoreProgress[key];
        if (isNaN(score) || !isFinite(score)) {
          if (data.length > 0) {
            data.push(data[data.length - 1]);
          } else {
            data.push(0);
          }
        } else {
          data.push(parseInt(score));
        }
      }
    }

    const chartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          borderColor: getTextColor(),
          backgroundColor: getTextColor(),
        },
        {
          data: [0],
          withDots: false,
        },
        {
          data: [100],
          withDots: false,
        },
      ],
    };

    return chartData;
  }

  const chartData = getChartData();
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme === 'light' ? theme?.background?.light : theme?.background?.dark }}>
      <div className="grid grid-cols-12 gap-[24px] px-[24px] py-6">
        <div className="col-span-12 md:col-span-6">
          <motion.div
            whileHover="hover"
            variants={cardVariants}
            className="rounded-xl overflow-hidden"
          >
            <div
              className="h-auto p-6 flex items-center justify-center"
              style={getCardBackground()}
            >
              <SpeedoMeter data={score} question={profile?.readiness_scores} />
            </div>
          </motion.div>

          {featureList?.dashboard.readiness_progress_history && <>
            <h2 className="mb-4 mt-10 text-xl font-semibold" style={{ color: getTextColor() }}>
              {STRINGS.readiness_progress_history}
            </h2>

            {Object.keys(scoreProgress).length > 0 ? (
              <motion.div 
                whileHover="hover"
                variants={cardVariants}
                className="rounded-xl overflow-hidden hidden md:flex w-full"
                style={getCardBackground()}
              >
                <div className="p-6 w-full">
                  <Chart data={chartData} />
                </div>
              </motion.div>
            ) : (
              <p 
                className="mb-4 hidden md:flex line-clamp-1 truncate my-10 text-base"
                style={{ color: getTextColor() }}
              >
                {STRINGS.domain_readiness_score_empty}
              </p>
            )}
          </>}

        </div>
        <div className="col-span-12 md:col-span-6 gap-[24px]">

          {featureList?.dashboard.domain_wise_readiness_score && 
            <motion.div 
              whileHover="hover"
              variants={cardVariants}
              className="rounded-xl overflow-hidden w-full"
              style={getCardBackground()}
            >
              <div className="p-6">
                <h2 className="mb-6 text-xl font-semibold" style={{ color: getTextColor() }}>
                  {STRINGS.domain_readiness_score}
                </h2>
                <div className="space-y-6">
                  {(score?.topic_progress ?? []).length > 0 &&
                    score?.topic_progress?.map((e, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="flex-1 truncate overflow-hidden mr-2 text-base font-medium" style={{ color: getTextColor() }}>
                            {e?.domain_name}
                          </span>
                          <span className="text-base font-medium" style={{ color: getTextColor() }}>
                            {(e?.readiness_score ?? 0) > 100
                              ? "100%"
                              : `${Math.round(
                                parseFloat(e?.readiness_score) ?? 0
                              )}%`}
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 dark:bg-gray-500 rounded-full">
                          <div
                            className={`h-2 rounded-full ${getProgressBarColor(
                              parseInt(e?.readiness_score ?? 0)
                            )}`}
                            style={{
                              width: `${(e?.readiness_score ?? 0) > 100
                                  ? "100%"
                                  : `${Math.round(
                                    parseFloat(e?.readiness_score) ?? 0
                                  )}%`
                                }`,
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                </div>

                {(score?.topic_progress ?? []).length === 0 && (
                  <p className="text-base font-medium" style={{ color: getTextColor() }}>
                    {STRINGS.domain_readiness_score_empty}
                  </p>
                )}
              </div>
            </motion.div>
          }

          {featureList?.dashboard.readiness_progress_history && <>
            <h2 
              className="mb-4 flex md:hidden line-clamp-1 truncate text-lg font-semibold"
              style={{ color: getTextColor() }}
            >
              {STRINGS.readiness_progress_history}
            </h2>
            <motion.div 
              whileHover="hover"
              variants={cardVariants}
              className="flex md:hidden w-full mt-6 rounded-xl overflow-hidden"
              style={getCardBackground()}
            >
              <div className="p-6 w-full">
                <Chart />
              </div>
            </motion.div>
          </>}

        </div>
      </div>
    </div>
  );
}

export default Dashboard;
